import { useEffect, useRef } from "react";

export const FiberCanvas = ({ className = "", density = 16000 }) => {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext("2d");
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        let raf;
        let w = 0;
        let h = 0;
        let nodes = [];
        const mouse = { x: -9999, y: -9999 };
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const init = () => {
            w = canvas.offsetWidth;
            h = canvas.offsetHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const count = Math.max(24, Math.min(85, Math.floor((w * h) / density)));
            nodes = Array.from({ length: count }, () => ({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * 0.45,
                vy: (Math.random() - 0.5) * 0.45,
                r: Math.random() * 1.6 + 0.7,
                hue: Math.random() < 0.72 ? "0,240,255" : "230,28,36",
            }));
        };

        const step = () => {
            ctx.clearRect(0, 0, w, h);
            for (const n of nodes) {
                n.x += n.vx;
                n.y += n.vy;
                const dx = mouse.x - n.x;
                const dy = mouse.y - n.y;
                const d2 = dx * dx + dy * dy;
                if (d2 < 42000 && d2 > 1) {
                    n.x += dx * 0.0007;
                    n.y += dy * 0.0007;
                }
                if (n.x < -20) n.x = w + 20;
                if (n.x > w + 20) n.x = -20;
                if (n.y < -20) n.y = h + 20;
                if (n.y > h + 20) n.y = -20;
            }

            for (let i = 0; i < nodes.length; i += 1) {
                const a = nodes[i];
                for (let j = i + 1; j < nodes.length; j += 1) {
                    const b = nodes[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const d = Math.hypot(dx, dy);
                    if (d < 150) {
                        const alpha = (1 - d / 150) * 0.32;
                        ctx.strokeStyle =
                            a.hue === b.hue
                                ? `rgba(${a.hue},${alpha})`
                                : `rgba(140,120,220,${alpha * 0.7})`;
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.stroke();
                    }
                }
            }

            for (const n of nodes) {
                ctx.beginPath();
                ctx.fillStyle = `rgba(${n.hue},0.9)`;
                ctx.shadowColor = `rgba(${n.hue},0.85)`;
                ctx.shadowBlur = 9;
                ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
                ctx.fill();
                ctx.shadowBlur = 0;
            }
            raf = requestAnimationFrame(step);
        };

        const onMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            mouse.x = e.clientX - rect.left;
            mouse.y = e.clientY - rect.top;
        };
        const onLeave = () => {
            mouse.x = -9999;
            mouse.y = -9999;
        };

        init();
        if (!reduced) {
            raf = requestAnimationFrame(step);
        } else {
            step();
            cancelAnimationFrame(raf);
        }
        window.addEventListener("resize", init);
        window.addEventListener("pointermove", onMove, { passive: true });
        window.addEventListener("pointerleave", onLeave);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", init);
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerleave", onLeave);
        };
    }, [density]);

    return <canvas ref={ref} className={className} aria-hidden="true" />;
};
